import {
  useGetSdlConfig,
  useGetTranslationConfig,
} from "../../components/hooks";
import { Label } from "../atoms";
import { Box, Flex } from "../cores";
import { SearchFilter, SelectFilter } from "../molecules";
import CheckboxFilter from "../molecules/Filters/CheckboxFilter";
import { useTableContext } from "../providers";
import { IColumnValues, IKeyStringAndValue, IOptions } from "../types/Types";

interface IFilterCollection {
  allCategories: IColumnValues;
  filteredCategories: IColumnValues;
}

type Entry = {
  key: string;
  label: string;
};

const FilterCollection = (data: IFilterCollection) => {
  const { listView } = useTableContext();
  const filters = useGetSdlConfig(listView).data as Array<IOptions>;
  const { filterOptions, setFilterOptions, inputValue, setInputValue } =
    useTableContext();
  let indexForCheckbox = 0;
  let indexForSelect = 0;
  const translations = useGetTranslationConfig(listView)
    .data as Array<IKeyStringAndValue>;
  const getTranslationCategories = (entryKey: string) =>
    translations.reduce(
      (
        acc: Array<string>,
        element: {
          [key: string]: string;
        }
      ) => {
        if (element.name.toLocaleLowerCase() === entryKey.toLocaleLowerCase())
          acc = [...acc, element.translation];
        return acc;
      },
      []
    );
  const checkIfPumpFilter = filters
    .map((item) => item.key)
    .includes("typeOfPump");

  // Search field case
  const Search = (entry: Entry) => {
    return (
      <Box
        sx={{
          gridColumnStart: "1",
          "> *:first-of-type": { marginY: "xs" },
        }}
        key={entry.key}
      >
        <Label htmlFor={entry.key}>{entry.label}</Label>
        <SearchFilter
          filter={entry.key}
          setFilterOptions={setFilterOptions}
          filterOptions={filterOptions}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />
      </Box>
    );
  };

  //Checkbox cases
  const Checkbox = (entry: Entry) => {
    indexForCheckbox = indexForCheckbox + 1;
    return (
      <Flex
        sx={{
          alignItems: "center",
          marginRight: 16,
          gridColumnStart: indexForCheckbox === 1 ? "1" : "auto",
          "> *:first-of-type": {
            marginY: "xs",
          },
        }}
        key={entry.key}
      >
        <CheckboxFilter
          id={entry.label}
          filter={entry.key}
          //@ts-ignore
          categories={data.allCategories}
          setFilterOptions={setFilterOptions}
          filterOptions={filterOptions}
        />
        <Label htmlFor={entry.key}>{entry.label}</Label>
      </Flex>
    );
  };

  // inRange cases with xx-yy from translation
  const InRangeSelect = (entry: Entry) => {
    return (
      <Box sx={{ "> *:first-of-type": { marginY: "xs" } }} key={entry.key}>
        <Label htmlFor={entry.key}>{entry.label}</Label>
        <SelectFilter
          id={entry.label}
          filter={entry.key}
          allCategories={
            entry.key === "typeOfPump"
              ? data.allCategories[entry.key]
              : getTranslationCategories(entry.key)
          }
          setFilterOptions={setFilterOptions}
          filterOptions={filterOptions}
          allOption={
            entry.key !== "typeOfPump" &&
            listView !== "test_tabeller_utomhusfarger"
          }
          noSort={entry.key === "omrade"}
        />
      </Box>
    );
  };

  // Equal and range start/end filter cases
  const DefaultSelect = (entry: Entry) => {
    indexForSelect = indexForSelect + 1;
    return (
      <Box
        sx={{
          gridColumnStart: indexForSelect === 1 ? "1" : "auto",
          "> *:first-of-type": {
            marginY: "xs",
          },
        }}
        key={entry.key}
      >
        <Label htmlFor={entry.key}>{entry.label}</Label>
        <SelectFilter
          id={entry.label}
          filter={entry.key}
          setFilterOptions={setFilterOptions}
          filterOptions={filterOptions}
          allCategories={
            entry.key === "make"
              ? data.allCategories[entry.key]
              : data.filteredCategories[entry.key]
          }
          filteredCategories={data.filteredCategories[entry.key]}
          checkFirstSelect={entry.key === "make" ? true : false}
        />
      </Box>
    );
  };

  const setFilter: {
    [key: string]: (entry: Entry) => JSX.Element;
  } = {
    model_version: Search,
    "four-wheel_drive": Checkbox,
    gearbox: Checkbox,
    backsensor: Checkbox,
    backcamera: Checkbox,
    good_choise: Checkbox,
    safest_choice: Checkbox,
    artisan_car: Checkbox,
    climate_bonus_car: Checkbox,
    traction_weight: InRangeSelect,
    spacegrade: InRangeSelect,
    spacegrade_max: InRangeSelect,
    spacegrade_min: InRangeSelect,
    maxload: InRangeSelect,
    markeffekt: InRangeSelect,
    typeOfPump: InRangeSelect,
    omrade: InRangeSelect,
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F2EEE3",
        borderRadius: 12,
        padding: 30,
        boxShadow:
          "0px 2px 5px -1px rgba(50, 50, 93, 0.25), 0px 1px 3px -1px rgba(0, 0, 0, 0.3)",

        margin: "md",
        display: "grid",
        gridTemplateColumns:
          checkIfPumpFilter || listView === "test_tabeller_varmesystem"
            ? ["100%", null, "repeat(2, 1fr)", "repeat(3, 1fr)", null]
            : listView === "test_tabeller_kgos"
            ? ["100%", null, "repeat(2, 1fr)"]
            : [
                "100%",
                null,
                "repeat(2, 1fr)",
                "repeat(3, 1fr)",
                "repeat(5, 1fr)",
              ],
        gap: 16,
      }}
    >
      {filters.map((entry) => {
        if (entry.filter) {
          return setFilter[entry.key]?.(entry) ?? DefaultSelect(entry);
        } else return null;
      })}
    </Box>
  );
};

export default FilterCollection;
